// domain/constants/serviceCode.js

/*
 * The two axes a service code is classified on. They answer different questions — `codeSystem` says
 * which published standard the code string is drawn from, `origin` says who is accountable for the
 * record existing — and neither is derivable from the other. A managed code is drawn from a standard
 * we do not own, and an org-authored one may perfectly well carry an HCPCS-shaped string without
 * being an HCPCS code.
 *
 * That last case is exactly why they stay two fields rather than one. Today every `custom` record
 * carries a null `codeSystem`, which makes the axes look redundant from inside one clinic's data;
 * they are not. Collapsing them would mean a state Medicaid programme publishing its waiver set —
 * a real published standard that arrives as a new `codeSystem` member — could not be told from a
 * local list somebody typed, and both would still be org-authored.
 */

/**
 * Which published code set the code string belongs to.
 *
 * A STRING IS NOT UNIQUE ACROSS SYSTEMS. `97153` is a CPT procedure code; a same-shaped string can
 * exist in another set meaning something unrelated. This is one of the two reasons a code's identity
 * is its own id rather than its string (the other being that one string can name two codes across a
 * revision — see ServiceCode.types).
 *
 * DIAGNOSIS CODES ARE NOT MEMBERS, deliberately, and their absence is the boundary rather than an
 * omission. An ICD-10 code has no unit basis, never carries a charge, and lands in a different box on
 * the claim (21 / 24E, not 24D) — putting it here would give a whole population a null basis and put
 * two concepts in one collection, which is the exact category error this entity was created to undo
 * one level up. A diagnosis catalog is the same PATTERN and a different ENTITY. This axis exists so
 * that arriving at one later is additive rather than a migration.
 *
 * MEMBERS NAME A PUBLISHED SET PRECISELY, not a family. `hcpcs_ii` rather than `hcpcs`, because
 * HCPCS Level I *is* CPT — a bare `hcpcs` would name a superset containing another member, which is
 * the kind of overlap that makes a discriminator stop discriminating. And `cpt_iii` is separate from
 * `cpt` because Category III codes are temporary by design: they sunset unless converted, so they
 * are the population an industry changeover acts on first. A field that could not tell them apart
 * would make "which of our codes are at risk" a question nobody could answer from the data.
 *
 * AN ORG-AUTHORED CODE CARRIES NULL, and no `local` member exists to give it. The codes this covers
 * — California regional center, Pennsylvania ODP waiver, workers' comp, school district — are
 * real and permanent, and they are precisely NOT published sets: each is one payer's local list,
 * unversioned, undistributed, and identical strings across two of them mean unrelated things. A
 * `local` member would therefore name no set at all, which is the one thing a discriminator cannot
 * do — it would classify every org code identically while telling a reader nothing, and the first
 * invariant below would have to be dropped to admit it.
 *
 * Null is the honest answer to "which published set is this drawn from": none. It also keeps this
 * axis meaning exactly what it says, so a genuine local STANDARD arriving later (a state publishing
 * its waiver set) joins as a real member rather than being absorbed into a bucket.
 *
 * @domainObject value-object
 * @invariant Extension is additive, and a new system joins as a peer rather than by generalizing an
 *   existing member into something set-neutral.
 * @invariant Members name a PROCEDURE code set. A set whose codes are not billable services belongs
 *   to its own entity, not to a new member here.
 * @invariant No member is a superset of another. Two overlapping sets cannot classify a code.
 * @typedef {'cpt' | 'cpt_iii' | 'hcpcs_ii'} CodeSystem
 */
export const CODE_SYSTEM = {
  CPT: 'cpt',
  CPT_III: 'cpt_iii',
  HCPCS_II: 'hcpcs_ii',
};

export const CODE_SYSTEM_VALUES = Object.values(CODE_SYSTEM);

export const isCodeSystem = (value) => CODE_SYSTEM_VALUES.includes(value);

/**
 * Who is accountable for this code record existing — us, or a customer.
 *
 * BOTH MEMBERS ARE WRITTEN. `managed` is the catalog: seeded and maintained by us through the
 * admin SDK, and unwritable from a browser. `custom` is an org-authored code — clinic-scoped, minted
 * by a customer for a code set our catalog does not carry and never will.
 *
 * THE GOVERNANCE THAT LET `custom` ARRIVE, since it was deferred for a real reason. A code identity
 * is a CROSS-ORGANIZATION reference key: benchmarks, care-model work and analytics all key on "the
 * same code" meaning the same thing everywhere, so an org minting its own `97153` would silently
 * corrupt every figure derived from it — and silently is the operative word, because nothing throws
 * and the numbers are merely wrong. Three things together made the two sets safe to hold in one
 * collection: an org code is scoped to its clinic and resolves nowhere else; the mint flow surfaces
 * any managed code sharing the typed string before one is created (`serviceCodeGovernance`); and the
 * by-string lookup PREFERS managed, so a shadow can never capture a resolution that was ours.
 *
 * Two orgs' identical local strings are not a governance problem and are deliberately left alone.
 * Neither is governed, so nothing rolls up through either — they are different things that look
 * alike, which is the ordinary case for a local set.
 *
 * NOT A PERMISSION, THOUGH THE RULES NOW READ IT. It records provenance — what the record IS, true
 * of it whoever is reading and whatever the rules allow that day. Authority is DERIVED from it in
 * `firestore.rules` (only a `custom` document is writable from a browser, and only by its clinic)
 * and that derivation lives there, not here. The distinction matters for the next member: adding one
 * describes a new provenance, it does not grant a write path, and whoever adds it has to decide the
 * rule separately and on purpose.
 *
 * @domainObject value-object
 * @invariant `managed` records are ours to create, retire and re-describe; a customer never writes
 *   one. Enforced by the rules gating every client write on `origin == 'custom'`, not by this value.
 * @invariant A `custom` record is meaningful only inside its owning clinic. It carries a `clinic`
 *   and resolves nowhere else; a `managed` record carries none and resolves everywhere.
 * @invariant Extension is additive. A third provenance (a partner-supplied set, say) is a new member
 *   rather than a reinterpretation of `custom`.
 * @typedef {'managed' | 'custom'} ServiceCodeOrigin
 */
export const SERVICE_CODE_ORIGIN = {
  MANAGED: 'managed',
  CUSTOM: 'custom',
};

export const SERVICE_CODE_ORIGIN_VALUES = Object.values(SERVICE_CODE_ORIGIN);

export const isServiceCodeOrigin = (value) => SERVICE_CODE_ORIGIN_VALUES.includes(value);
